import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import RoundButton from 'features/shared/components/buttons/RoundButton';
import DateFormatter from 'features/shared/components/formatters/DateFormatter';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import RequirementSimplifiedDTO from '../dtos/RequirementSimplifiedDTO';

import styles from './requirementsimplified.module.scss';
import RenderWrapper from 'features/shared/components/renderWrapper/RenderWrapper';

interface Props {
  requirement: RequirementSimplifiedDTO;
  enableDescription?: boolean;
}

const RequirementSimplified = ({ requirement, enableDescription = false }: Props) => {
  // translations
  const tTerms = useTranslation(["shared\\terms"]).t;

  // sub renders
  const renderState = (requirement: RequirementSimplifiedDTO):ReactElement => {
    if (requirement.resolveDate) {
      return (
        <>
          <span className="d-inline-block rounded-circle p-1 mr-1 bg-success"></span>
          <small className="text-success text-truncate d-inline-block" title={requirement.actualState}>
            {requirement.actualState}
          </small>
        </>
      );
    } else {
      return (
        <>
          <span className="d-inline-block rounded-circle p-1 mr-1 bg-warning"></span>
          <small className="text-warning text-truncate d-inline-block" title={requirement.actualState}>
            {requirement.actualState}
          </small>
        </>
      );
    }
  }
  
  const renderDescription = (description?: string) => {
    if (description) {
      return <div className={`small text-muted ${styles.descriptionItem}`}>{requirement.descriptionShort}</div>;
    } else {
      return <div className={`small text-muted ${styles.descriptionItem}`}>{tTerms("Undefined")}</div>;
    }
  }
  
  const renderResolveDate = () => {
    if (requirement.resolveDate) {
      return <DateFormatter date={requirement.resolveDate} />;
    } else {
      return <>-</>
    }
  }

  // render
  return (
    <div className="row no-gutters align-items-center">
      <div className="col">
        <div className="row no-gutters align-items-center">
          <div className="col-auto col-sm-3 col-lg-2 pr-2">
            <div className="d-flex flex-wrap flex-sm-column">
              <span className="mr-2 mr-lg-0">
                <span className="mr-1 text-muted">
                  <FontAwesomeIcon icon="hourglass-start" size="xs" fixedWidth />
                </span>
                <span className="small">
                  <DateFormatter date={requirement.issueDate!} />
                </span>
              </span>
              <span className="mt-lg-n1">
                <span className="mr-1 text-muted">
                  <FontAwesomeIcon icon="hourglass-end" size="xs" fixedWidth />
                </span>
                <span className="small">
                  {renderResolveDate()}
                </span>
              </span>
            </div>
          </div>
          <div className="col-auto col-sm-3 col-lg-2 pr-sm-2 text-truncate">
            <span className="mr-2 mr-lg-0">
              <span className="d-sm-none mr-1 text-muted">
                <FontAwesomeIcon icon="tag" size="xs" fixedWidth />
              </span>
              <span className={styles.numberItem} title={requirement.requirementNumber}>
                {requirement.requirementNumber}
              </span>
            </span>
          </div>
          <div className="col-sm-6 col-lg-8">
            <div className="row no-gutters align-items-center">
              <div className={`px-lg-3 col-lg-6${enableDescription ? " col-xl-4" : ""}`}> 
                <div className="text-truncate" title={requirement.requirementType?.name}>
                  {requirement.requirementType?.name}
                </div>
              </div>
              <div className={`col-lg-6${enableDescription ? " col-xl-3" : ""}`}>
                <div className="d-flex align-items-center">
                  {renderState(requirement)}
                </div>
              </div>
              <RenderWrapper isVisible={() => enableDescription}>
                <div className="d-none d-xl-block col-xl-5 pl-lg-4">
                  {renderDescription(requirement.descriptionShort)}
                </div>
              </RenderWrapper>
            </div>
          </div>
        </div>
      </div>
      <div className="col-auto pl-2 pl-lg-3">
        <RoundButton asLink to={`requirements/${requirement.requirementId}`} />
      </div>
    </div>
  )
}

export default RequirementSimplified;
